import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import EmailInputField from "../../components/EmailInputField";
import PasswordInputField from "../../components/PasswordInputField";
import CTAs from "../../components/CTAs"; // reusable button component
import { userService } from "../../services/userService";
import { subscriptionService } from "../../services/subscriptionService";
import Footer from "../../components/Footer";

function LogInPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Redirect user to correct dashboard after login
  const checkAndRedirectUser = useCallback(
    async (user) => {
      const userId = user?.id || user?._id;
      if (!userId || !user?.email || !user?.name) return false;

      try {
        if (user.selectedBlock === "professional_services") {
          localStorage.setItem("user_id", user.id);
          navigate("/debtordashboard", { state: user });
        } else {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/lba/check-user/${user.id}`,
          );
          await response.json();
          navigate("/debtordashboard", { state: user });
        }
        return true;
      } catch (err) {
        console.error("Redirect failed:", err);
        return false;
      }
    },
    [navigate],
  );

  // Resume checkout with the same provider the company originally used.
  // errorData.provider is expected to be "stripe" | "paystack" | "revolut"
  // (falls back to stripe if the backend hasn't started sending it yet).
  const resumeCheckout = useCallback(async (companyId, provider) => {
    switch (provider) {
      case "paystack":
        return subscriptionService.createPaystackCheckoutSession(companyId);
      case "revolut":
        return subscriptionService.createRevolutCheckoutSession(companyId);
      case "stripe":
      default:
        return subscriptionService.createCheckoutSession(companyId);
    }
  }, []);

  // Main login handler
  const handleLoginClick = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const user = await userService.login(email, password);

      if (user.selectedBlock === "professional_services") {
        localStorage.setItem("user_id", user.id);
      }

      if (rememberMe) {
        localStorage.setItem("rememberMe", "true");
      } else {
        localStorage.removeItem("rememberMe");
      }

      await checkAndRedirectUser(user);
    } catch (err) {
      if (err.message && err.message.includes("subscription")) {
        const errorData = JSON.parse(err.message);
        if (errorData.redirectToCheckout && errorData.companyId) {
          const confirmCheckout = window.confirm(
            errorData.error +
              "\n\nWould you like to complete your subscription now?",
          );
          if (confirmCheckout) {
            try {
              const checkoutResult = await resumeCheckout(
                errorData.companyId,
                errorData.provider,
              );
              const redirectUrl =
                checkoutResult.url || checkoutResult.checkoutUrl;
              if (redirectUrl) {
                window.location.href = redirectUrl;
              } else {
                alert("Checkout failed. Contact support.");
              }
            } catch (checkoutErr) {
              alert("Checkout failed. Contact support.");
              console.error(checkoutErr);
            }
          }
        } else {
          alert(errorData.error || "Subscription issue.");
        }
      } else {
        alert(err.message || "Invalid Username Or Password");
      }

      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [email, password, rememberMe, checkAndRedirectUser, isLoading, resumeCheckout]);

  // Auto‑login on page reload if rememberMe was set
  useEffect(() => {
    const autoLogin = async () => {
      const token = localStorage.getItem("authToken");
      const remember = localStorage.getItem("rememberMe");

      if (!token || !remember) return;

      try {
        const cachedUserResponse = await userService.getMeWithCache();
        const user = cachedUserResponse?.data || cachedUserResponse;
        if (user) {
          await checkAndRedirectUser(user);
        }
      } catch (err) {
        console.error("Auto login failed:", err);
        localStorage.removeItem("authToken");
        localStorage.removeItem("authUser");
        localStorage.removeItem("rememberMe");
      }
    };

    autoLogin();
  }, [checkAndRedirectUser]);

  const handleRegisterClick = useCallback(() => {
    navigate("/selectaccount");
  }, [navigate]);

  const handleForgotPasswordClick = useCallback(() => {
    navigate("/forgetpassword");
  }, [navigate]);

  const handleEmailChange = (e) => setEmail(e.target.value);
  const handlePasswordChange = (e) => setPassword(e.target.value);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');

        html, body {
          margin: 0;
          background: #ffffff !important;
        }

        .dd-root {
          font-family: 'DM Sans', sans-serif;
          background: #ffffff;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0 20px;
        }

        .dd-form-container {
          width: 400px;
          max-width: 100%;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          gap: 24px;
          margin: auto;
        }

        .dd-section-label {
          font-size: 14px;
          font-weight: 700;
          color: #0F172A;
          margin-bottom: 6px;
          display: block;
        }

        .dd-required { color: #EF4444; margin-left: 2px; }

        .dd-input-group { display: flex; flex-direction: column; gap: 6px; }

        .dd-input-wrapper { position: relative; width: 100%; }

        .dd-input {
          width: 100%;
          box-sizing: border-box;
          padding: 12px 14px;
          border: 1px solid #E2E8F0;
          border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          color: #1E293B;
          background: #FFFFFF;
          outline: none;
          transition: border-color 0.15s;
        }
        .dd-input:focus {
          border-color: #2563EB;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
        }
        .dd-input.dd-input-error {
          border-color: #EF4444;
          padding-right: 36px;
        }
        .dd-input.dd-input-error:focus {
          border-color: #EF4444;
          box-shadow: 0 0 0 3px rgba(239,68,68,0.1);
        }

        .dd-error-icon {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: #EF4444;
          display: flex;
        }

        .dd-error-msg {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: #EF4444;
          margin-top: 4px;
        }

        /* Buttons removed – styles are handled inside CTAs component */

        .dd-checkbox-label {
          display: flex; align-items: center; gap: 8px;
          font-size: 13px; color: #1E293B; cursor: pointer;
        }
        .dd-checkbox {
          width: 16px; height: 16px; accent-color: #2563EB;
        }
        .dd-link {
          font-size: 13px; font-weight: 600; color: #2563EB;
          text-decoration: none; cursor: pointer;
        }
        .dd-link:hover { text-decoration: underline; }
        .dd-text { font-size: 13px; color: #64748B; }
      `}</style>

      <div className="dd-root">
        <div className="dd-form-container">
          <div style={{ textAlign: "center", marginBottom: "8px" }}>
            <img
              src="assets/main-logo.svg"
              alt="Logo"
              style={{ height: "40px" }}
            />
          </div>

          <div style={{ textAlign: "center" }}>
            <h1
              style={{
                fontSize: "24px",
                fontWeight: 700,
                color: "#0F172A",
                margin: "0 0 8px 0",
              }}
            >
              Log in to your account
            </h1>
            <p className="dd-text">Welcome back! Please enter your details.</p>
          </div>

          <EmailInputField
            title="Email"
            placeholder="name@yourcompany.com"
            value={email}
            onChange={handleEmailChange}
            width="100%"
          />

          <PasswordInputField
            title="Password"
            placeholder="Enter your password"
            value={password}
            onChange={handlePasswordChange}
            width="100%"
          />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <label className="dd-checkbox-label">
              <input
                type="checkbox"
                className="dd-checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              Remember for 30 days
            </label>
            <span className="dd-link" onClick={handleForgotPasswordClick}>
              Forgot password
            </span>
          </div>

          {/* Reusable CTAs component with gradient style */}
          <CTAs
            type="Signinbtn"
            text={isLoading ? "Logging in..." : "Log in"}
            width="100%"
            onClick={handleLoginClick}
            disabled={isLoading}
          />

          <div style={{ textAlign: "center" }}>
            <span className="dd-text">Don't have an account? </span>
            <span className="dd-link" onClick={handleRegisterClick}>
              Register
            </span>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}

export default LogInPage;